import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, Lightbulb, Users } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import BlogHeader from "@/components/blog/BlogHeader";
import BlogFooter from "@/components/blog/BlogFooter";
import heroLeadCapture from "@/assets/blog/hero-lead-capture.jpg";
import heroLeadMagnets from "@/assets/blog/hero-lead-magnets.jpg";
import heroPdfs from "@/assets/blog/hero-pdfs-que-vendem.jpg";
import heroFunilVendas from "@/assets/blog/hero-funil-vendas.jpg";
import heroIAQualificacao from "@/assets/blog/hero-ia-qualificacao-leads.jpg";
import heroMelhorFerramenta from "@/assets/blog/hero-melhor-ferramenta-formulario.jpg";
import heroCustoAutomatizar from "@/assets/blog/hero-custo-nao-automatizar.jpg";
import heroLandingPages from "@/assets/blog/hero-landing-pages-convertem.jpg";
import heroDiagnosticoIA from "@/assets/blog/hero-diagnostico-ia-consultoria.jpg";
import heroFormulariosDinamicos from "@/assets/blog/hero-formularios-dinamicos.jpg";
import heroFormularioCapturaLeads from "@/assets/blog/hero-formulario-captura-leads.jpg";
import heroAtencaoSemAnuncios from "@/assets/blog/hero-atencao-sem-anuncios.jpg";
import heroQuizQualificaLeads from "@/assets/blog/hero-quiz-qualifica-leads.jpg";

const blogPosts = [
  {
    slug: "como-criar-quiz-que-qualifica-leads-automaticamente",
    title: "Como Criar um Quiz que Qualifica Leads Automaticamente: Guia Completo de 2026",
    excerpt: "Estrutura, perguntas que realmente qualificam, lead scoring, IA adaptativa e entrega de diagnóstico personalizado. Passo a passo para capturar leads já prontos para comprar.",
    category: "Qualificação de Leads",
    categoryColor: "primary" as const,
    readTime: "16 min",
    date: "2026-08-18",
    image: heroQuizQualificaLeads,
    imageAlt: "Ilustração 3D de ímã de ferradura branco com pontas vermelha e azul atraindo cartões de quiz com alternativas marcadas e avatares entrando em um funil roxo",
  },
  {
    slug: "como-conquistar-atencao-sem-gastar-mais-em-anuncios",
    title: "Como Conquistar Atenção Sem Gastar Mais em Anúncios: 9 Estratégias para 2026",
    excerpt: "O CPM sobe todo ano e o orçamento não. Veja como conquistar atenção sem gastar mais em anúncios usando conteúdo útil, IA, parcerias e experiências interativas.",
    category: "Tráfego & Atenção",
    categoryColor: "accent" as const,
    readTime: "15 min",
    date: "2026-08-08",
    image: heroAtencaoSemAnuncios,
    imageAlt: "Ilustração 3D de ímã de ferradura branco com pontas vermelha e azul atraindo ícones de olhos, balões de comentário, corações, megafone e avatares de pessoas sobre fundo gradiente roxo",
  },
  {
    slug: "como-criar-formulario-de-captura-de-leads-que-converte",
    title: "Como Criar um Formulário de Captura de Leads que Converte de Verdade em 2026",
    excerpt: "Guia prático 2026: estrutura, copy, campos ideais, IA, mobile-first e testes A/B. Passo a passo para triplicar a conversão do seu formulário de captura de leads.",
    category: "Captura de Leads",
    categoryColor: "primary" as const,
    readTime: "14 min",
    date: "2026-07-21",
    image: heroFormularioCapturaLeads,
    imageAlt: "Ilustração 3D de ímã de ferradura vermelho e azul atraindo campos de formulário, envelope e cartão de contato sobre fundo gradiente roxo",
  },
  {
    slug: "diagnostico-com-ia-para-consultores-capturar-leads",
    title: "Diagnóstico com IA: O Lead Magnet que Lota a Agenda de Consultores em 2026",
    excerpt: "Diagnósticos personalizados com IA convertem até 3x mais que e-books. Veja a anatomia, exemplos reais e o passo a passo para criar o seu em 1 dia.",
    category: "Lead Magnets & IA",
    categoryColor: "accent" as const,
    readTime: "18 min",
    date: "2026-06-01",
    image: heroDiagnosticoIA,
    imageAlt: "Ilustração 3D de ímã branco atraindo relatório de diagnóstico com gráficos, cérebro e badges de porcentagem sobre fundo gradiente roxo e azul",
  },
  {
    slug: "como-criar-formularios-dinamicos-que-convertem",
    title: "Formulários Dinâmicos: Como Criar em 2026 e Triplicar Conversões",
    excerpt: "Aprenda a criar formulários que se adaptam ao usuário, qualificam leads em tempo real e convertem até 3x mais. Guia prático com IA, lógica condicional e exemplos reais.",
    category: "UX & Formulários",
    categoryColor: "primary" as const,
    readTime: "16 min",
    date: "2026-05-15",
    image: heroFormulariosDinamicos,
    imageAlt: "Ilustração 3D de ímã branco atraindo campos de formulário flutuantes — caixas de texto, dropdown, checkbox, slider e balão de pergunta — sobre fundo gradiente roxo e azul",
  },
  {
    slug: "landing-pages-que-convertem-em-2026",
    title: "Landing Pages que Convertem: O Guia Completo de 2026",
    excerpt: "Anatomia, copy, design mobile-first, formulários inteligentes, prova social e testes A/B — tudo para construir landing pages que dobram sua conversão em 2026.",
    category: "Conversão & CRO",
    categoryColor: "primary" as const,
    readTime: "34 min",
    date: "2026-05-05",
    image: heroLandingPages,
    imageAlt: "Ilustração 3D de ímã branco atraindo elementos de uma landing page sobre fundo gradiente roxo e azul",
  },
  {
    slug: "quanto-custa-nao-automatizar-sua-empresa",
    title: "Quanto Custa Não Automatizar Sua Empresa em 2026?",
    excerpt: "Descubra o custo invisível de operar sem automação: horas perdidas, leads que escapam, erros humanos e oportunidades de receita. Calculadora prática e exemplos reais.",
    category: "Automação & Produtividade",
    categoryColor: "accent" as const,
    readTime: "32 min",
    date: "2026-04-13",
    image: heroCustoAutomatizar,
    imageAlt: "Ilustração 3D de ímã branco atraindo moedas de ouro, engrenagens e relógio, simbolizando custo do tempo e dinheiro sem automação",
  },
  {
    slug: "melhor-ferramenta-de-formulario-para-seu-negocio",
    title: "Descubra Qual é a Melhor Ferramenta de Formulário para Seu Negócio",
    excerpt: "Comparativo completo entre MagnetLink, Typeform, Google Forms, Jotform, Tally e mais. Ranking personalizado com análise de recursos e funcionalidades.",
    category: "Ferramentas & Comparativos",
    categoryColor: "accent" as const,
    readTime: "35 min",
    date: "2026-03-01",
    image: heroMelhorFerramenta,
    imageAlt: "Ilustração de ímã 3D branco atraindo elementos abstratos de formulários digitais sobre fundo gradiente roxo e azul",
  },
  {
    slug: "como-aplicar-estrategias-captura-de-leads",
    title: "Como Aplicar Estratégias de Captura de Leads: Guia Completo",
    excerpt: "Aprenda tudo sobre captura de leads com magnet links. Descubra estratégias comprovadas, exemplos práticos e como criar formulários inteligentes que convertem.",
    category: "Marketing Digital",
    categoryColor: "primary" as const,
    readTime: "15 min",
    date: "2026-02-14",
    image: heroLeadCapture,
    imageAlt: "Ilustração de funil de captura de leads com ímã atraindo pessoas e emails",
  },
  {
    slug: "ia-para-qualificacao-de-leads",
    title: "IA para Qualificação de Leads: Como Vender Mais em 2026",
    excerpt: "Descubra como usar inteligência artificial para qualificar leads automaticamente. Estratégias, ferramentas e tendências para fechar 2025 e entrar em 2026 vendendo mais.",
    category: "Inteligência Artificial",
    categoryColor: "primary" as const,
    readTime: "28 min",
    date: "2026-01-05",
    image: heroIAQualificacao,
    imageAlt: "Ilustração de inteligência artificial analisando e qualificando leads com funil de dados e gráficos analíticos",
  },
  {
    slug: "como-criar-pdfs-que-vendem",
    title: "Como Criar PDFs que Vendem: Guia Completo para 2026",
    excerpt: "Aprenda a criar PDFs profissionais que capturam leads e vendem. Técnicas de design, copywriting, IA generativa e tendências de 2026.",
    category: "PDFs & Design",
    categoryColor: "primary" as const,
    readTime: "25 min",
    date: "2025-12-10",
    image: heroPdfs,
    imageAlt: "Ilustração de documento PDF flutuando com efeitos de atração magnética e ícones de marketing digital",
  },
  {
    slug: "funil-de-vendas-black-friday-2025",
    title: "Funil de Vendas para Black Friday 2025: Guia Completo",
    excerpt: "Aprenda a construir um funil de vendas completo para a Black Friday 2025. Estratégias de captação, nutrição e conversão de leads com dicas práticas e tendências.",
    category: "Funil de Vendas",
    categoryColor: "accent" as const,
    readTime: "30 min",
    date: "2025-11-01",
    image: heroFunilVendas,
    imageAlt: "Ilustração de funil de vendas para Black Friday com etapas de conversão e ícones de desconto",
  },
  {
    slug: "os-10-melhores-lead-magnets-que-convertem",
    title: "Os 10 Melhores Lead Magnets que Convertem em 2025",
    excerpt: "Descubra os 10 tipos de lead magnets mais eficazes para capturar e converter leads. Guia completo com exemplos, estratégias e dicas práticas.",
    category: "Lead Magnets",
    categoryColor: "accent" as const,
    readTime: "20 min",
    date: "2025-10-05",
    image: heroLeadMagnets,
    imageAlt: "Ilustração de ímã atraindo diversos tipos de lead magnets como checklists, templates e presentes",
  },
];

const Blog = () => {
  return (
    <>
      <SEOHead
        title="Blog | Magnet Link - Estratégias de Captura de Leads"
        description="Dicas, estratégias e tutoriais sobre captura de leads, formulários inteligentes e marketing digital. Aprenda a converter mais visitantes em clientes."
        keywords="blog, marketing digital, captura de leads, conversão, formulários"
        canonicalUrl="/blog"
      />
      
      <div className="min-h-screen bg-background">
        <BlogHeader />
        
        {/* Hero Section */}
        <section 
          className="pt-32 pb-16 md:pt-40 md:pb-20 bg-gradient-to-b from-primary/5 to-background"
          aria-labelledby="blog-title"
        >
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-4 py-1.5 text-sm">
                <Lightbulb className="h-4 w-4 text-primary" aria-hidden="true" />
                <span>Conteúdo Educativo</span>
              </div>
              <h1 
                id="blog-title"
                className="mb-6 text-4xl font-bold tracking-tight md:text-5xl"
              >
                Blog Magnet Link
              </h1>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
                Estratégias, tutoriais e dicas para transformar visitantes em leads qualificados 
                usando formulários inteligentes e IA.
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <main 
          className="py-16 md:py-24"
          role="main"
          aria-label="Lista de artigos do blog"
        >
          <div className="container mx-auto px-4">
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {blogPosts.map((post) => (
                <article 
                  key={post.slug}
                  className="group"
                >
                  <Link 
                    to={`/blog/${post.slug}`}
                    className="block h-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-xl"
                  >
                    <Card className="h-full overflow-hidden border-border/50 bg-card/50 backdrop-blur transition-all hover:border-primary/50 hover:shadow-lg">
                      {/* Cover Image */}
                      <div className="aspect-video relative overflow-hidden">
                        <img 
                          src={post.image}
                          alt={post.imageAlt}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                        <span className={`absolute bottom-3 left-3 rounded-full px-2.5 py-1 text-xs font-medium ${
                          post.categoryColor === "accent" 
                            ? "bg-accent text-accent-foreground" 
                            : "bg-primary text-primary-foreground"
                        }`}>
                          {post.category}
                        </span>
                      </div>
                      
                      <CardContent className="p-5">
                        <div className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" aria-hidden="true" />
                            <time dateTime={post.date}>
                              {(() => {
                                const [y, m, d] = post.date.split('-').map(Number);
                                return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
                              })()}
                            </time>
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" aria-hidden="true" />
                            {post.readTime}
                          </span>
                        </div>

                        <h2 className="mb-3 text-xl font-semibold group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        
                        <p className="mb-4 text-sm text-muted-foreground line-clamp-2">
                          {post.excerpt}
                        </p>

                        <span className="inline-flex items-center gap-2 text-sm font-medium text-primary group-hover:text-primary/80">
                          Ler Artigo
                          <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </main>

        {/* Newsletter Section */}
        <section 
          className="py-16 bg-muted/30"
          aria-labelledby="newsletter-title"
        >
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-xl">
              <div className="mb-4 flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Users className="h-6 w-6 text-primary" aria-hidden="true" />
                </div>
              </div>
              <h2 
                id="newsletter-title"
                className="mb-4 text-2xl font-bold"
              >
                Quer Aprender Mais?
              </h2>
              <p className="mb-6 text-muted-foreground">
                Experimente criar seu primeiro formulário inteligente e veja na prática 
                como capturar leads qualificados.
              </p>
              <Button asChild className="gap-2">
                <Link to="/auth?mode=signup">
                  Criar Formulário Grátis
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <BlogFooter />
      </div>
    </>
  );
};

export default Blog;
